import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Trash2, Plus, Coffee } from "lucide-react";
import { PLATFORM_META, PLATFORM_KEYS } from "@/lib/socials";
import IconStylePicker from "@/components/admin/IconStylePicker";
import TipJarCard from "@/components/TipJarCard";

export default function FooterPanel() {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/site-settings/footer").then(r => setState(r.data));
  useEffect(() => { load(); }, []);

  const persist = async (next) => {
    setState(next);
    setSaving(true);
    try { await api.put("/site-settings/footer", next); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); load(); }
    finally { setSaving(false); }
  };
  const setTagline = (v) => persist({ ...state, tagline: v });
  const setCopyright = (v) => persist({ ...state, copyright: v });
  const addSocial = () => {
    const used = new Set(state.socials.map(s => s.platform));
    const next = PLATFORM_KEYS.find(k => !used.has(k));
    if (!next) { toast.error("All platforms already added"); return; }
    persist({ ...state, socials: [...state.socials, { platform: next, url: "" }] });
  };
  const updateSocial = (i, changes) => {
    const socials = state.socials.map((s, idx) => idx === i ? { ...s, ...changes } : s);
    persist({ ...state, socials });
  };
  const removeSocial = (i) => {
    persist({ ...state, socials: state.socials.filter((_, idx) => idx !== i) });
  };

  if (!state) return <div className="mt-6 text-slate-500">Loading…</div>;

  return (
    <Card className="border-slate-200 mt-6">
      <CardHeader>
        <CardTitle className="font-heading flex items-center justify-between">
          <span>Footer</span>
          <span className="text-xs uppercase tracking-widest text-slate-400 font-normal">
            {saving ? "Saving…" : "Auto-saves"}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1">
            Copyright line
          </label>
          <Input
            data-testid="footer-copyright"
            value={state.copyright || ""}
            onChange={e => setCopyright(e.target.value)}
            placeholder="© {year} Hospice Synopsis. Story · Strategy · Soul."
            maxLength={200}
            className="max-w-2xl"
          />
          <p className="text-xs text-slate-400 mt-1">
            Shown in the bottom-left of every page. Use <code className="bg-slate-100 px-1 rounded">{"{year}"}</code> to insert the current year automatically.
          </p>
          {state.copyright && (
            <div className="mt-2 text-xs text-slate-500 border border-slate-200 rounded px-3 py-2 bg-slate-50 max-w-2xl">
              <span className="uppercase tracking-widest text-[10px] text-slate-400 mr-2">Preview</span>
              {(state.copyright || "").replace(/\{year\}/g, String(new Date().getFullYear()))}
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1">
            Tagline
          </label>
          <Input
            data-testid="footer-tagline"
            value={state.tagline}
            onChange={e => setTagline(e.target.value)}
            placeholder="e.g. Clinical Minimal Edition"
            maxLength={120}
            className="max-w-md"
          />
          <p className="text-xs text-slate-400 mt-1">Shown in the bottom-right of every page.</p>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs uppercase tracking-widest text-slate-500">Social links</label>
            <button data-testid="footer-add-social" onClick={addSocial}
              className="text-xs uppercase tracking-widest text-sky-700 hover:text-sky-900 inline-flex items-center gap-1">
              <Plus className="w-3.5 h-3.5"/>Add link
            </button>
          </div>
          {state.socials.length === 0 ? (
            <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded p-6 text-center">
              No social links yet. Click <strong>Add link</strong> to add one.
            </div>
          ) : (
            <ul className="space-y-2">
              {state.socials.map((s, i) => {
                const meta = PLATFORM_META[s.platform];
                const Icon = meta?.Icon;
                return (
                  <li key={i} data-testid={`footer-social-row-${i}`}
                      className="flex items-center gap-2 border border-slate-200 rounded-lg p-2">
                    <div className="w-8 h-8 grid place-items-center text-slate-500 shrink-0">
                      {Icon && <Icon className="w-4 h-4"/>}
                    </div>
                    <select
                      data-testid={`footer-social-platform-${i}`}
                      value={s.platform}
                      onChange={e => updateSocial(i, { platform: e.target.value })}
                      className="text-sm border border-slate-200 rounded px-2 py-1.5 bg-white min-w-[160px]"
                    >
                      {PLATFORM_KEYS.map(k => (
                        <option key={k} value={k}>{PLATFORM_META[k].label}</option>
                      ))}
                    </select>
                    <Input
                      data-testid={`footer-social-url-${i}`}
                      value={s.url}
                      onChange={e => updateSocial(i, { url: e.target.value })}
                      placeholder={meta?.placeholder}
                      className="flex-1"
                    />
                    <button data-testid={`footer-social-remove-${i}`} onClick={() => removeSocial(i)}
                            className="text-red-500 hover:text-red-700 shrink-0" title="Remove">
                      <Trash2 className="w-4 h-4"/>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <p className="text-xs text-slate-400 mt-2">Icons render in monochrome slate in the footer.</p>
        </div>

        <div className="pt-6 border-t border-slate-200">
          <TipJarSettings state={state} persist={persist} />
        </div>

        <div className="pt-6 border-t border-slate-200">
          <IconStylePicker/>
        </div>
      </CardContent>
    </Card>
  );
}

function TipJarSettings({ state, persist }) {
  const tj = state.tip_jar || { enabled: false, amounts_cents: [300, 500, 1000], allow_custom: true };
  const set = (patch) => persist({ ...state, tip_jar: { ...tj, ...patch } });
  const amts = Array.isArray(tj.amounts_cents) && tj.amounts_cents.length ? tj.amounts_cents : [300, 500, 1000];
  const updateAmount = (i, dollarsStr) => {
    const n = parseFloat(dollarsStr);
    const list = [...amts];
    list[i] = Number.isFinite(n) ? Math.round(n * 100) : 0;
    set({ amounts_cents: list.filter(v => v > 0) });
  };
  const addAmount = () => set({ amounts_cents: [...amts, 1500].slice(0, 5) });
  const removeAmount = (i) => set({ amounts_cents: amts.filter((_, idx) => idx !== i) });

  return (
    <div data-testid="footer-tip-jar-settings">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-md bg-amber-100 grid place-items-center shrink-0">
          <Coffee className="w-4 h-4 text-amber-800"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-heading font-semibold text-slate-900">Buy Me a Coffee</div>
              <p className="text-xs text-slate-500 mt-0.5">Add a tip jar to the footer of every page. You can also drop the same card inside any blog or page section.</p>
            </div>
            <label className="inline-flex items-center gap-2 text-sm shrink-0">
              <input
                type="checkbox"
                data-testid="footer-tip-jar-toggle"
                checked={!!tj.enabled}
                onChange={e => set({ enabled: e.target.checked })}
              />
              <span>{tj.enabled ? "Visible" : "Hidden"}</span>
            </label>
          </div>
        </div>
      </div>

      {tj.enabled && (
        <div className="mt-5 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1">Heading</label>
              <Input
                data-testid="footer-tip-jar-heading"
                value={tj.heading || ""}
                onChange={e => set({ heading: e.target.value.slice(0, 80) })}
                placeholder="Enjoying Hospice Synopsis?"
                maxLength={80}
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1">Button label</label>
              <Input
                data-testid="footer-tip-jar-button-label"
                value={tj.button_label || ""}
                onChange={e => set({ button_label: e.target.value.slice(0, 32) })}
                placeholder="Buy me a coffee"
                maxLength={32}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1">Body</label>
            <textarea
              data-testid="footer-tip-jar-body"
              value={tj.body || ""}
              onChange={e => set({ body: e.target.value.slice(0, 280) })}
              rows={2}
              maxLength={280}
              className="w-full border border-slate-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
              placeholder="Short pitch, one or two sentences."
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1">
              Amount presets <span className="normal-case text-slate-400">(dollars, up to 5)</span>
            </label>
            <div className="flex flex-wrap items-center gap-2" data-testid="footer-tip-jar-amounts">
              {amts.map((c, i) => (
                <div key={i} className="flex items-center gap-1">
                  <span className="text-slate-500 text-sm">$</span>
                  <Input
                    data-testid={`footer-tip-jar-amount-${i}`}
                    type="number"
                    min="1"
                    max="500"
                    step="1"
                    value={(c / 100).toString()}
                    onChange={e => updateAmount(i, e.target.value)}
                    className="w-20"
                  />
                  {amts.length > 1 && (
                    <button type="button" onClick={() => removeAmount(i)}
                            data-testid={`footer-tip-jar-amount-remove-${i}`}
                            className="text-xs text-slate-400 hover:text-red-600"
                            aria-label="Remove amount">×</button>
                  )}
                </div>
              ))}
              {amts.length < 5 && (
                <button type="button" onClick={addAmount}
                        data-testid="footer-tip-jar-amount-add"
                        className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-50">
                  + Add amount
                </button>
              )}
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              data-testid="footer-tip-jar-allow-custom"
              checked={tj.allow_custom !== false}
              onChange={e => set({ allow_custom: e.target.checked })}
            />
            Allow visitors to enter a custom amount
          </label>

          <div className="pt-3 border-t border-slate-100">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Live preview</div>
            <TipJarCard
              heading={tj.heading || "Enjoying Hospice Synopsis?"}
              body={tj.body || "This site is free, ad-light, and built by one person. If it's helped you, you can say thanks with a coffee."}
              buttonLabel={tj.button_label || "Buy me a coffee"}
              amountsCents={amts}
              allowCustom={tj.allow_custom !== false}
            />
          </div>
        </div>
      )}
    </div>
  );
}
